import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { isBoolean } from 'lodash';
import { getClosedPositionInvalidReasonToText } from '@monorepo/client-common';
import { Icons } from '@monorepo/icons';
import {
  Flex,
  Label,
  Space,
  Statistic,
  TextComponent,
  Tooltip,
} from '@monorepo/react-components';
import {
  LoadingState,
  useLoading,
  usePagination,
  useSort,
} from '@monorepo/react-hooks';
import {
  Application,
  ClosedPosition,
  ClosedPositionStatus,
  ThemeColors,
  TimeZone,
} from '@monorepo/types';
import { IconsNames } from '@monorepo/types';
import { Table } from '../../components/table/table';
import { useStore } from '../../helpers/use-store';
import { useColor } from '../../hooks';
import TradesTable from '../trades-table/trades-table';
import { POSITIONS_TABLE_SCROLL_HEIGHT } from './positions.constants';
import { PositionSideTag } from './positions.util';

import './trades-positions.scss';

export interface Props {
  application?: Application;
  accountId: string;
  expandTrades?: boolean;
  hideSwap?: boolean;
  contest?: boolean;
}

const ClosedPositionsTable: FunctionComponent<Props> = ({
  application,
  accountId,
  expandTrades = true,
  hideSwap = false,
  contest = false,
}) => {
  const {
    dataStore: { positionStore },
  } = useStore();

  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const { getColor } = useColor();
  const { page, limit, total, setTotal, setLimit, setPage } = usePagination();
  const { sort, onSortChange } = useSort<ClosedPosition>({
    sortField: 'closeDate',
  });
  const { loadingState, updateLoadingState } = useLoading();
  const { modal } = App.useApp();
  const { t } = useTranslation(['position', 'common']);

  const isThe5ers = application === Application.THE5ERS;
  const relevantGetClosedPositions = contest
    ? positionStore.getContestClosedPositions
    : positionStore.getClosedPositions;

  useEffect(() => {
    const requestPage = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        const response = await relevantGetClosedPositions(accountId, {
          page,
          limit,
          sort,
        });
        updateLoadingState(LoadingState.Loaded);
        setClosedPositions(response.results);
        setTotal(response.total);
      } catch {
        updateLoadingState(LoadingState.Error);
      }
    };
    requestPage();
  }, [page, limit, sort, accountId]);

  const columns: ColumnsType<ClosedPosition> = [
    {
      title: t('position:positions.id'),
      dataIndex: 'id',
      render: (_, position) => {
        return (
          <TextComponent level={8} responsive={false} weight={600}>
            {position.id || position.externalId}
          </TextComponent>
        );
      },
    },
    {
      title: t('position:positions.open_date'),
      dataIndex: 'openDate',
      sorter: true,
      render: (_, position) => {
        let date = dayjs(position.openDate).format('MMM D hh:mm A');

        if (isThe5ers) {
          date = dayjs(position.openDate)
            .tz(TimeZone.Kyiv)
            .format('MMM D HH:mm:ss');
        }

        return <span>{date}</span>;
      },
    },
    {
      title: t('position:positions.close_date'),
      dataIndex: 'closeDate',
      sorter: true,
      render: (_, position) => {
        let date = dayjs(position.closeDate).format('MMM D hh:mm A');

        if (isThe5ers) {
          date = dayjs(position.closeDate)
            .tz(TimeZone.Kyiv)
            .format('MMM D HH:mm:ss');
        }

        return <span>{date}</span>;
      },
    },
    {
      title: t('position:positions.symbol'),
      dataIndex: 'symbol',
      sorter: true,
      render: (_, position) => {
        return <Label color="colorInfo">{position.symbol}</Label>;
      },
    },
    {
      title: t('position:positions.side'),
      dataIndex: 'side',
      sorter: true,
      render: (_, position) => {
        return <PositionSideTag side={position.side} />;
      },
    },
    {
      title: t('position:positions.entry'),
      dataIndex: 'entry',

      render: (_, position) => {
        return (
          <Statistic value={position.entry} precision={isThe5ers ? 4 : 2} />
        );
      },
    },
    {
      title: t('position:positions.exit'),
      dataIndex: 'exit',

      render: (_, position) => {
        return (
          <Statistic value={position.exit} precision={isThe5ers ? 4 : 2} />
        );
      },
    },
    {
      title: t('position:positions.qty'),
      dataIndex: 'quantity',
    },
    {
      title: t('common:fee'),
      dataIndex: 'fee',
      render: (_, position) => {
        return <Statistic value={position.fee} precision={2} prefix={'$'} />;
      },
    },
    !hideSwap
      ? {
          title: t('position:positions.swap'),
          dataIndex: 'swap',
          render: (_, position) => {
            return (
              <Statistic
                value={position.swap || 0}
                precision={2}
                prefix={'$'}
              />
            );
          },
        }
      : {},
    {
      title: t('common:pnl'),
      dataIndex: 'profitAndLoss',
      sorter: true,
      render: (_, position) => {
        const isInvalid = isBoolean(position.isValid) && !position.isValid;
        const invalidReasons = position.invalidReasons || [];
        const stat = (
          <TextComponent
            color={isInvalid ? getColor(ThemeColors.colorError) : undefined}
            weight={600}
          >
            <Statistic
              value={position.profitAndLoss}
              precision={2}
              prefix={'$'}
            />
          </TextComponent>
        );

        if (isInvalid && invalidReasons.length > 0) {
          const TooltipTexts = invalidReasons.map((reason) => (
            <TextComponent>
              {getClosedPositionInvalidReasonToText({
                reason,
                tradeDuration: position.minTradeDuration,
                t,
              })}
            </TextComponent>
          ));

          return (
            <Tooltip
              title={<Space direction={'vertical'}>{TooltipTexts}</Space>}
            >
              {stat}
            </Tooltip>
          );
        }

        return stat;
      },
    },
    {
      title: t('common:status'),
      dataIndex: 'status',
      render: (_, position) => {
        const isWin = position.profitAndLoss > 0;

        const status = isWin
          ? ClosedPositionStatus.Win
          : ClosedPositionStatus.Loss;
        const icon = isWin ? IconsNames.CaretUp : IconsNames.CaretDown;

        return (
          <Label color={isWin ? 'colorSuccess' : 'colorActive'}>
            <Flex align="baseline" gap={5}>
              <TextComponent level={6} responsive={false}>
                <Icons iconName={icon} />
              </TextComponent>
              {status}
            </Flex>
          </Label>
        );
      },
    },
  ];

  const isLoading = loadingState === LoadingState.Loading;

  return (
    <Table
      label={
        <Flex align="center" gap={4}>
          <TextComponent responsive={false} level={3}>
            <Icons iconName={IconsNames.Lock} />
          </TextComponent>
          <TextComponent responsive={false} level={4}>
            {t('position:positions.closed_trades')}
          </TextComponent>
        </Flex>
      }
      size={'middle'}
      scroll={{ y: POSITIONS_TABLE_SCROLL_HEIGHT }}
      onChange={(pagination, _filters, sorter) => {
        if (pagination.current) {
          setPage(pagination.current);
        }
        if (pagination.pageSize) {
          setLimit(pagination.pageSize);
        }
        onSortChange(sorter);
      }}
      pagination={{
        showSizeChanger: true,
        current: page,
        pageSize: limit,
        total,
      }}
      onRow={(record: ClosedPosition) => {
        return {
          onClick: () => {
            if (!expandTrades) {
              return;
            }

            modal.info({
              width: '80%',
              title: t('position:positions.position_details_for', {
                symbol: record.symbol,
              }),
              closable: true,
              maskClosable: true,
              content: (
                <TradesTable
                  positionState={'closed'}
                  accountId={accountId}
                  positionId={record.id}
                />
              ),
              okText: t('common:close'),
            });
          },
        };
      }}
      loading={isLoading}
      columns={columns.filter((col) => col.title)}
      dataSource={closedPositions}
      rowKey="id"
      rowHoverable={expandTrades}
    />
  );
};

export default ClosedPositionsTable;
